import React, { useCallback, useEffect, useRef, useState } from "react";
import { Breadcrumb, Button, Divider, Input, Modal, Space, Spin, Typography, Badge, Tag as AntTag, message, Table, Tooltip } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import axios from "axios";
import ParseLogo from "../shared/images/parse.png";

const { Title } = Typography;

const LIST_QUERY = `
  query FindImageTags($first: Int!, $skip: Int, $where: Editor_images_tagsWhereInput, $order: [Editor_images_tagsOrder!]) {
    findImageTags(first: $first, skip: $skip, where: $where, order: $order) {
      count
      edges {
        node {
          objectId
          name
          img { url }
          isMainCategory
          isMainCategoryImg
        }
      }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const CREATE_MUTATION = `
  mutation CreateTag($name: String!) {
    createEditor_images_tags(input: { fields: { name: $name } }) {
      editor_images_tags { objectId name }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const UPDATE_MUTATION = `
  mutation UpdateTag($id: ID!, $name: String!) {
    updateEditor_images_tags(input: { id: $id, fields: { name: $name } }) {
      editor_images_tags { objectId name }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const GiottoTags = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [searchName, setSearchName] = useState("");
  const searchInput = useRef(null);
  const [sortOrder, setSortOrder] = useState('ascend'); // 'ascend' | 'descend'
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editTagName, setEditTagName] = useState("");

  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();
  const pageSize = 100;

  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  const fetchPage = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const token = await getTokenSilently();
      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: searchName ? { name: { matchesRegex: searchName, options: "i" } } : undefined,
        order: sortOrder === 'descend' ? ["name_DESC"] : ["name_ASC"],
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: { query: LIST_QUERY, variables },
        headers: { Authorization: `Bearer ${token}` },
      });

      const connection = response.data?.findImageTags;
      if (connection) {
        const newItems = (connection.edges || []).map((e) => e.node);
        setCount(connection.count || 0);
        setItems(newItems);
      }
    } catch (error) {
      console.error("Error loading tags", error);
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl, pageSize, searchName, sortOrder]);

  useEffect(() => {
    setItems([]);
    setCount(0);
    setCurrentPage(1);
    fetchPage(1);
  }, [fetchPage]);

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name) {
      message.warning("Inserisci un nome per il tag");
      return;
    }
    try {
      const token = await getTokenSilently();
      await axios({
        method: "post",
        url: graphqlUrl,
        data: { query: CREATE_MUTATION, variables: { name } },
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Tag creato con successo");
      setModalOpen(false);
      setNewTagName("");
      // Reload list from start to reflect sorting by name
      setItems([]);
      setCount(0);
      setCurrentPage(1);
      fetchPage(1);
    } catch (error) {
      console.error("Error creating tag", error);
      message.error("Errore nella creazione del tag");
    }
  };

  // AntD column search helpers
  const handleSearch = (selectedKeys, confirm) => {
    confirm();
    const value = (selectedKeys?.[0] || "").trim();
    setSearchName(value);
    setCurrentPage(1);
    fetchPage(1);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchName("");
    setCurrentPage(1);
    fetchPage(1);
  };

  const getColumnSearchProps = () => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Cerca nome"
          value={selectedKeys?.[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon={<SearchOutlined />}
            size="small"
          >
            Cerca
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">Reset</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    filteredValue: searchName ? [searchName] : null,
  });

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
      sorter: true,
      sortOrder: sortOrder,
      ...getColumnSearchProps(),
    },
    {
      title: 'Immagine',
      dataIndex: 'img',
      key: 'img',
      render: (img, record) => img?.url ? (
        <img
          src={img.url}
          alt={record.name}
          style={{
            height: 48,
            maxWidth: 120,
            objectFit: 'contain',
            objectPosition: 'center',
            background: '#fff',
            borderRadius: 4,
            boxShadow: 'rgba(0, 0, 0, 0.06) 0px 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 2px 6px',
          }}
        />
      ) : null,
      width: 160,
    },
    {
      title: 'Categoria',
      dataIndex: 'isMainCategory',
      key: 'isMainCategory',
      render: (val) => val ? <AntTag color="purple">Principale</AntTag> : null,
      width: 140,
    },
    {
      title: 'Immagine Principale',
      dataIndex: 'isMainCategoryImg',
      key: 'isMainCategoryImg',
      render: (val) => val ? <AntTag color="geekblue">Img Cat.</AntTag> : null,
      width: 180,
    },
    {
      title: 'Parse',
      key: 'parse',
      width: 70,
      render: (_, record) => {
        const filters = encodeURIComponent(
          JSON.stringify([
            { field: "objectId", constraint: "eq", compareTo: record.objectId, class: "editor_images_tags" },
          ])
        );
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/editor_images_tags?filters=${filters}`;
        return (
          <Tooltip title="Apri in Parse Dashboard">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <img src={ParseLogo} width={16} alt="Parse Dashboard" />
            </a>
          </Tooltip>
        );
      }
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditRecord(record);
              setEditTagName(record.name || "");
              setEditModalOpen(true);
            }}
          >
            Rinomina
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div>{loading && <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>}</div>
      <Breadcrumb style={{ marginBottom: "20px" }}>
        <Breadcrumb.Item>Giotto</Breadcrumb.Item>
        <Breadcrumb.Item>Tags</Breadcrumb.Item>
      </Breadcrumb>
      <Divider />
      <Title level={3} style={{ marginBottom: "20px" }}>
        <Space>
          Tags
          <Badge overflowCount={5000} count={count} />
        </Space>
      </Title>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Aggiungi Tag
        </Button>
      </Space>
      <Divider />
      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{
          total: count,
          current: currentPage,
          pageSize,
          showSizeChanger: false,
          onChange: (page) => {
            setCurrentPage(page);
            fetchPage(page);
          },
        }}
        onChange={(pagination, filters, sorter) => {
          const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
          if (order && order !== sortOrder) {
            setSortOrder(order);
            setCurrentPage(1);
            fetchPage(1);
          }
        }}
      />

      <Modal
        title="Nuovo Tag"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={createTag}
        okText="Crea"
        cancelText="Annulla"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <label>Nome</label>
          <Input
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            placeholder="Inserisci il nome del tag"
            maxLength={80}
          />
        </Space>
      </Modal>
      <Modal
        title="Rinomina Tag"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={async () => {
          const name = editTagName.trim();
          if (!name || !editRecord) {
            message.warning("Inserisci un nome valido");
            return;
          }
          try {
            const token = await getTokenSilently();
            await axios({
              method: "post",
              url: graphqlUrl,
              data: { query: UPDATE_MUTATION, variables: { id: editRecord.objectId, name } },
              headers: { Authorization: `Bearer ${token}` },
            });
            message.success("Tag rinominato");
            setEditModalOpen(false);
            setEditRecord(null);
            setEditTagName("");
            fetchPage(currentPage);
          } catch (err) {
            console.error("Error updating tag", err);
            message.error("Errore durante la rinomina del tag");
          }
        }}
        okText="Salva"
        cancelText="Annulla"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <label>Nuovo nome</label>
          <Input
            value={editTagName}
            onChange={(e) => setEditTagName(e.target.value)}
            placeholder="Inserisci il nuovo nome"
            maxLength={80}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default GiottoTags;
